import React from "react";
import PropTypes from "prop-types";
import MentionAutocomplete from "../../../../components/MentionAutocomplete";
import Button from "../../../../components/ui/Button";
import "./CommentBox.css";

import { useCommentThread } from "./hooks/useCommentThread";
import SortControls from "./components/SortControls";
import CommentThread from "./components/CommentThread";
import Pagination from "./components/Pagination";

const CommentBox = ({ postId, isVisible = true }) => {
  const { state, derived, refs, handlers } = useCommentThread({
    postId,
    isVisible,
  });

  const {
    isLoading,
    commentText,
    sortOrder,
    isEditing,
    editedText,
    showOptions,
    isExpanded,
    isSubmitting,
    showReplyInput,
    replyToUser,
    mentionMap,
    COMMENT_MAX_LENGTH,
    INITIAL_DISPLAY_COUNT,
  } = state;

  const {
    totalComments,
    remainingComments,
    hasMoreOnServer,
    visibleComments,
    displayedComments,
    deleteConfirmState,
    savedSet,
    currentUser,
    commentLikePending,
    likedCommentIds,
  } = derived;

  const { commentInputRef, optionsMenuRef, commentSectionRef } = refs;

  const {
    setCommentText,
    setSortOrder,
    setIsEditing,
    setEditedText,
    setShowOptions,
    handleLoadMoreComments,
    handleShowLessComments,
    handleToggleSaveComment,
    handleCommentSubmit,
    handleCommentEdit,
    handleCommentLike,
    toggleReplies,
    handleDeleteClick,
    handleMentionSelect,
    isCommentOwner,
  } = handlers;

  return (
    <div
      ref={commentSectionRef}
      className={`comment-section ${
        deleteConfirmState.isOpen && deleteConfirmState.itemType === "comment"
          ? "modal-open"
          : ""
      }`}
    >
      <div className="comments-header">
        <h3 className="comments-title">Comments</h3>
        <SortControls sortOrder={sortOrder} setSortOrder={setSortOrder} />
      </div>

      <div className="comment-input-container">
        <MentionAutocomplete
          ref={commentInputRef}
          className="post-comment-textarea form-textarea"
          placeholder="Write your comment…"
          value={commentText}
          maxLength={COMMENT_MAX_LENGTH}
          onChange={(newValue) => setCommentText(newValue)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              if (!isSubmitting) {
                handleCommentSubmit();
              }
            }
          }}
          onMentionSelect={handleMentionSelect}
          mentionNames={Object.keys(mentionMap)}
          aria-label="Write a comment"
        />
        <div className="char-counter-row">
          <span
            className={`char-counter${
              commentText.length >= COMMENT_MAX_LENGTH
                ? " at-limit"
                : commentText.length >= COMMENT_MAX_LENGTH * 0.9
                  ? " near-limit"
                  : ""
            }`}
          >
            {commentText.length}/{COMMENT_MAX_LENGTH}
          </span>
        </div>
        <Button
          variant="primary"
          onClick={handleCommentSubmit}
          disabled={!commentText.trim() || isSubmitting || !currentUser}
          className="comment-submit-button"
        >
          {isSubmitting
            ? "Posting..."
            : !currentUser
              ? "Sign in to comment"
              : "Comment"}
        </Button>
      </div>

      <CommentThread
        isLoading={isLoading}
        commentsLength={visibleComments.length}
        totalComments={totalComments}
        displayedComments={displayedComments}
        postId={postId}
        isEditing={isEditing}
        editedText={editedText}
        showOptions={showOptions}
        showReplyInput={showReplyInput}
        replyToUser={replyToUser}
        savedSet={savedSet}
        commentLikePending={commentLikePending}
        likedCommentIds={likedCommentIds}
        COMMENT_MAX_LENGTH={COMMENT_MAX_LENGTH}
        optionsMenuRef={optionsMenuRef}
        isCommentOwner={isCommentOwner}
        setShowOptions={setShowOptions}
        setIsEditing={setIsEditing}
        setEditedText={setEditedText}
        handleDeleteClick={handleDeleteClick}
        handleCommentEdit={handleCommentEdit}
        handleMentionSelect={handleMentionSelect}
        mentionMap={mentionMap}
        handleCommentLike={handleCommentLike}
        toggleReplies={toggleReplies}
        handleToggleSaveComment={handleToggleSaveComment}
      />

      <Pagination
        remainingComments={remainingComments}
        hasMoreOnServer={hasMoreOnServer}
        visibleCommentsCount={visibleComments.length}
        isLoading={isLoading}
        handleLoadMoreComments={handleLoadMoreComments}
        isExpanded={isExpanded}
        INITIAL_DISPLAY_COUNT={INITIAL_DISPLAY_COUNT}
        handleShowLessComments={handleShowLessComments}
      />
    </div>
  );
};

CommentBox.propTypes = {
  postId: PropTypes.string,
  isVisible: PropTypes.bool,
};

export default React.memo(CommentBox);
